import { useEffect, useState } from 'react';

import { SNAPSHOT_ITEMS, SNAPSHOT_IMAGES } from '../crafter-snapshot/index.js';

/*
 * The Crafter client: one item at a time, by its repository path, through Crafter's
 * REST content_store/item.json with flatten=true, so a page arrives with its embedded
 * blocks in one GET -- cacheable by esfront's nginx, one URL per item, and one file per
 * item in the snapshot.
 *
 * WHERE IT ASKS. Off Studio, `/crafter/item.json`: esfront's nginx (and the dev server)
 * proxies it to the delivery Engine and appends crafterSite. Inside Studio's preview
 * iframe the page runs on Studio's own origin, so it asks the preview Engine directly at
 * /api/1/... and the sandbox answers; the crafterPreview cookie Studio set rides along.
 *
 * WHAT IT ANSWERS. { root, source }: root is the item's own fields (descriptorDom's single
 * child, page or component), source 'crafter' or 'snapshot'. Anything but a 200 carrying a
 * descriptor -- a stopped Crafter, a 503 from an nginx with no Crafter origin, the SPA's
 * index.html from an nginx with no /crafter location -- reads the bundled snapshot of the same
 * path, and a path the snapshot lacks is null. Never a blank page, never a throw.
 *
 * Remembered for the session, a miss included: copy changes on publish, not mid-visit.
 */

export const CRAFTER_PREFIX = '/crafter';
export const CRAFTER_FALLBACK_LOCALE = 'en';

// The languages Crafter has pages for: the folders under /site/website/ the snapshot recorded.
// Folders are language-named (en, fr); a reader's full tag finds its folder by esCrafterFolder.
export const CRAFTER_LOCALES = [ ...new Set( Object.keys( SNAPSHOT_ITEMS )
    .map( ( pPath ) => /^\/site\/website\/([^/]+)\//.exec( pPath )?.[ 1 ] )
    .filter( Boolean ) ) ].sort( ( pA, pB ) => pA.localeCompare( pB ) );

const CRAFTER_PREVIEW_CHECK = '/api/1/config/preview.json';

let authoringAnswer = null;
const itemCache = new Map();

/** For tests: forget every remembered answer. */
export function esCrafterReset() {
    authoringAnswer = null;
    itemCache.clear();
}

/**
 * Whether this page is inside Studio's preview. Asked once, and only inside an iframe:
 * outside one the answer is no without a request, which is every visitor to the site.
 */
export function esCrafterIsAuthoring() {
    if ( authoringAnswer ) return authoringAnswer;

    const aFramed = typeof window !== 'undefined' && window.self !== window.top;
    authoringAnswer = !aFramed ? Promise.resolve( false )
        : fetch( CRAFTER_PREVIEW_CHECK, { credentials: 'include' } )
            .then( ( pResponse ) => pResponse.ok ? pResponse.json() : null )
            .then( ( pBody ) => Boolean( pBody?.preview ) )
            .catch( () => false );

    return authoringAnswer;
}

export const esCrafterPagePath = ( pLocale, pSlug ) => `/site/website/${ pLocale }/${ pSlug }/index.xml`;
export const esCrafterComponentPath = ( pLocale, pName ) => `/site/components/${ pLocale }/${ pName }.xml`;

function itemUrl( pPath, pAuthoring ) {
    const aQuery = 'url=' + encodeURIComponent( pPath ) + '&flatten=true';
    return pAuthoring
        ? '/api/1/site/content_store/item.json?' + aQuery
        : CRAFTER_PREFIX + '/item.json?' + aQuery;
}

/** descriptorDom's one child: the page or component's own fields. */
export function esCrafterRoot( pAnswer ) {
    const aDom = pAnswer?.descriptorDom;
    if ( !aDom || typeof aDom !== 'object' ) return null;

    const aRoot = Object.values( aDom )[ 0 ];
    return aRoot && typeof aRoot === 'object' ? aRoot : null;
}

async function fromSnapshot( pPath ) {
    const aLoad = SNAPSHOT_ITEMS[ pPath ];
    if ( !aLoad ) return null;

    const aRoot = esCrafterRoot( ( await aLoad() ).default );
    return aRoot ? { root: aRoot, source: 'snapshot', path: pPath } : null;
}

async function load( pPath ) {
    try {
        const aAuthoring = await esCrafterIsAuthoring();
        const aResponse = await fetch( itemUrl( pPath, aAuthoring ), { credentials: 'include' } );
        const aType = aResponse.headers?.get?.( 'content-type' ) ?? '';

        if ( aResponse.ok && aType.includes( 'json' ) ) {
            const aRoot = esCrafterRoot( await aResponse.json() );
            if ( aRoot ) return { root: aRoot, source: 'crafter', authoring: aAuthoring, path: pPath };
        }
    }
    catch {
        // Crafter not answering is a state, not a fault: the snapshot answers.
    }

    return fromSnapshot( pPath );
}

/** One item by repository path: { root, source, path, authoring } or null. */
export function esCrafterItem( pPath ) {
    if ( !itemCache.has( pPath ) ) itemCache.set( pPath, load( pPath ) );
    return itemCache.get( pPath );
}

/**
 * The Crafter folder a reader's tag reads: its language when Crafter has one (fr-CA reads fr),
 * else en. Crafter's folders are language-named; the reader's locale is a full BCP-47 tag.
 */
export function esCrafterFolder( pTag ) {
    const aLanguage = String( pTag ?? '' ).toLowerCase().split( /[-_]/ )[ 0 ];
    return CRAFTER_LOCALES.includes( aLanguage ) ? aLanguage : CRAFTER_FALLBACK_LOCALE;
}

/**
 * Whether Crafter has pages in pTag's language. A caller with a page of its own -- the nav, the
 * footer, home, legal -- asks before it asks Crafter: those write every word through copykeys,
 * which carry every live locale, and Crafter's en standing in would turn a German reader's
 * translated nav back into English (Tim, 2026-10-19: only en and fr took). A page only Crafter
 * has still falls back to en, which beats a 404. Read from the snapshot, as esCrafterFolder is,
 * so a language added in Studio counts once the snapshot is re-recorded.
 */
export const esCrafterHas = ( pTag ) => CRAFTER_LOCALES.includes( String( pTag ?? '' ).toLowerCase().split( /[-_]/ )[ 0 ] );

/**
 * The locale's item, else en's: a missing translation falls back one item at a time. pOwn
 * asks for the locale's item alone, for a caller with a page of its own to draw instead.
 */
export async function esCrafterLocalized( pPathFor, pTag, pOwn = false ) {
    const aLocale = esCrafterFolder( pTag );
    const aItem = await esCrafterItem( pPathFor( aLocale ) );
    if ( aItem || pOwn || aLocale === CRAFTER_FALLBACK_LOCALE ) return aItem;

    return esCrafterItem( pPathFor( CRAFTER_FALLBACK_LOCALE ) );
}

/**
 * An item for a component, while pEnabled. Never asks while disabled, so the switch off
 * costs no request at all.
 */
const DISABLED = Object.freeze( { item: null, loading: false } );

export function useEsCrafterLocalized( pPathFor, pTag, pEnabled, pOwn = false ) {
    const aFolder = esCrafterFolder( pTag );
    const [ state, setState ] = useState( { item: null, loading: pEnabled } );
    const aKey = pEnabled ? pPathFor( aFolder ) : null;

    useEffect( () => {
        // Disabled sets nothing: the switch off schedules no render at all, so off is
        // today's component update for update. The answer below ignores the state.
        if ( !pEnabled ) return undefined;

        let aLive = true;
        setState( ( pState ) => ( { ...pState, loading: true } ) );

        void esCrafterLocalized( pPathFor, aFolder, pOwn ).then( ( pItem ) => {
            if ( aLive ) setState( { item: pItem, loading: false } );
        } );

        return () => { aLive = false; };
        // pPathFor is a module-level function at every call site; aKey carries its answer.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ aKey, pEnabled, pOwn ] );

    return pEnabled ? state : DISABLED;
}

/**
 * A repeating group or node selector as an array. Crafter turns XML into JSON naively:
 * one <item> is an object, several an array, none an empty element.
 */
export function esCrafterList( pGroup ) {
    const aItems = pGroup?.item;
    if ( aItems === undefined || aItems === null ) return [];
    return Array.isArray( aItems ) ? aItems : [ aItems ];
}

/** A Crafter boolean, which arrives as the string 'true' or 'false'. */
export const esCrafterBool = ( pValue ) => pValue === true || pValue === 'true';

/** Text with {year} as this year: a copyright line written once is never stale. */
export const esCrafterText = ( pValue ) =>
    String( pValue ?? '' ).replace( /\{year\}/g, String( new Date().getFullYear() ) );

/**
 * A static asset's URL. A snapshot answer means Crafter did not, so its images come out of
 * the bundle by file name; Studio's preview serves /static-assets itself.
 */
export function esCrafterAsset( pPath, pItem ) {
    if ( !pPath ) return '';
    if ( pItem?.source === 'snapshot' ) {
        const aName = pPath.split( '/' ).pop();
        return SNAPSHOT_IMAGES[ aName ] ?? '';
    }
    return pItem?.authoring ? pPath : CRAFTER_PREFIX + pPath;
}

// The tags rich text may carry; any other keeps its text and loses itself and its attributes,
// and the dropped ones lose their content too. Authors are
// escapade admins, but a sanitizer is still the difference between copy and script.
const DROPPED_TAGS = new Set( [ 'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT' ] );
const ALLOWED_TAGS = new Set( [ 'P', 'UL', 'OL', 'LI', 'STRONG', 'EM', 'B', 'I', 'A', 'BR', 'H3', 'H4', 'H5', 'CODE', 'SPAN' ] );

function cleanNode( pNode, pDocument ) {
    if ( pNode.nodeType === 3 ) return pDocument.createTextNode( esCrafterText( pNode.textContent ) );
    if ( pNode.nodeType !== 1 || DROPPED_TAGS.has( pNode.tagName ) ) return null;

    const aChildren = Array.from( pNode.childNodes ).map( ( pChild ) => cleanNode( pChild, pDocument ) ).filter( Boolean );
    if ( !ALLOWED_TAGS.has( pNode.tagName ) ) {
        const aFragment = pDocument.createDocumentFragment();
        aChildren.forEach( ( pChild ) => aFragment.appendChild( pChild ) );
        return aFragment;
    }

    const aCopy = pDocument.createElement( pNode.tagName.toLowerCase() );
    if ( pNode.tagName === 'A' ) {
        const aHref = pNode.getAttribute( 'href' ) ?? '';
        if ( /^(https?:|mailto:|\/|#)/i.test( aHref ) ) aCopy.setAttribute( 'href', aHref );
        if ( /^https?:/i.test( aHref ) ) {
            aCopy.setAttribute( 'target', '_blank' );
            aCopy.setAttribute( 'rel', 'noreferrer' );
        }
        aCopy.setAttribute( 'class', 'underline decoration-dotted hover:text-accent-hover' );
    }
    aChildren.forEach( ( pChild ) => aCopy.appendChild( pChild ) );
    return aCopy;
}

/** Rich text as safe HTML, {year} filled. */
export function esCrafterHtml( pHtml ) {
    if ( !pHtml || typeof DOMParser === 'undefined' ) return '';

    const aDocument = new DOMParser().parseFromString( `<body>${ pHtml }</body>`, 'text/html' );
    const aOut = aDocument.createElement( 'div' );
    Array.from( aDocument.body.childNodes ).forEach( ( pChild ) => {
        const aClean = cleanNode( pChild, aDocument );
        if ( aClean ) aOut.appendChild( aClean );
    } );
    return aOut.innerHTML;
}

/**
 * Experience Builder's markers for one field, inside Studio's preview only; nothing on
 * the live site. A block's model is its embedded component, whose path is the page's.
 */
export function esCrafterXb( pItem, pComponent, pField, pIndex ) {
    if ( !pItem?.authoring || !pComponent ) return {};

    return {
        'data-craftercms-model-id': pComponent.objectId,
        'data-craftercms-model-path': pItem.path,
        ...( pField ? { 'data-craftercms-field-id': pField } : {} ),
        ...( pIndex !== undefined ? { 'data-craftercms-index': String( pIndex ) } : {} ),
    };
}
